import * as _ from 'lodash';

import type { ProjectGroupReleaseProject } from '../../../../../../shared/model-typings';
import type { ProjectsFilter } from '../../../../../../shared/projectsV2/filters';
import type { MinimalProject, Project } from '../../../../../../shared/projectsV2/response';

import type { AddedProjectIndex } from './types';

export type AnyProject = Project | MinimalProject | ProjectGroupReleaseProject;

/**
 * Check that a given project is a MinimalProject type.
 * We check that it has a `locator` and no `id`.
 *
 * @param {AnyProject} project - The project to check.
 */
export const isMinimalProject = (project: AnyProject): project is MinimalProject => {
  return !('id' in project) && 'locator' in project;
};

/**
 * This is a check for the ProjectGroupReleaseProject type.
 * It passes if there is no `id` or `locator` and there is a `projectId`,
 * which is fairly brittle and will need to be updated if the type changes.
 *
 * @param {AnyProject} project - The project to check.
 */
export const isReleaseProject = (project: AnyProject): project is ProjectGroupReleaseProject => {
  return !('id' in project) && !('locator' in project) && 'projectId' in project;
};

/**
 * This function takes any of the project types we deal with in the revamp,
 * and returns a normalized project locator.
 *
 * @param {AnyProject} project - The project to normalize.
 */
export const normalizeProjectLocator = (project: AnyProject): string => {
  if (isMinimalProject(project)) {
    return project.locator;
  }
  if (isReleaseProject(project)) {
    return project.projectId;
  }
  return project.id;
};

/**
 * Helper function to determine if any filters are applied
 * using the filter object that is passed directly to the BE.
 *
 * Note that title and locators are the only filters that are
 * defined by default and need to be checked for emptiness separately.
 * All other filters are optional and can be checked by _.isNil
 *
 * @returns true if any filter is present
 */
export const hasFiltersApplied = (filters: ProjectsFilter): boolean => {
  return Object.entries(filters).some(([key, value]) => {
    // title and locators are the only filters that are defined by default
    //  and need to be checked for emptiness separately
    if (key === 'title' || key === 'locators') {
      return !_.isEmpty(value);
    }

    // array filters must be checked for defined-ness and emptiness
    if (key === 'type' || key === 'labels' || key === 'teamId') {
      return !_.isEmpty(value) && !_.isNil(value);
    }

    // all other filters are optional and can be checked by _.isNil
    if (!_.isNil(value)) {
      return true;
    }

    return false;
  });
};

/**
 * Helper function to return the locators of added projects for use in the fetchProjects call.
 * @param showAddedProjects boolean toggle based on useReleaseGroupState and the users toggle choice
 * @param addedProjects object record of projects that are either added or staged to be added to a release group
 * @returns an array of locators to be used in the fetchProjects call as a ProjectFilter
 */
export const getLocatorsParam = (showAddedProjects: boolean, addedProjects: AddedProjectIndex): string[] => {
  // When showing only added projects, update the project filters to include
  // the locators of added projects. When NOT showing only added projects,
  // remove the locators filter so that all projects (not just added) are returned.
  // If there are no added projects, we still need to pass an empty string to the
  // locators filter so that no projects are returned.
  if (!showAddedProjects) {
    return [];
  }
  if (_.isEmpty(addedProjects)) {
    return [''];
  }
  return Object.keys(addedProjects);
};
